import { Functionality } from '@frontend/functionality/types';
import { Setting } from '@frontend/setting/types';
import { ScreenType, Step, Wizard } from '@frontend/wizard/types';
import { PayloadAction, createSlice } from '@reduxjs/toolkit';

export interface MobileWizardState {
    [key: string]: MobileWizardStateRecord;
}
export interface MobileWizardStateRecord {
    functionality: Functionality;
    functionalitySettings: Setting[];
    wizard: Wizard;
    steps: Step[];
    data: MobileWizardStateData;
    history?: ScreenType[];
    currentScreen: ScreenType;
    from?: string; // route from which the wizard was opened
    /** Browser history index of the page under the wizard (e.g. detail). Used to pop all wizard frames on finish. */
    returnHistoryIndex?: number;
}
export interface MobileWizardStateData {
    [key: string]: any; // Define the structure of the data as needed
}

const initialState: MobileWizardState = {};

const mobileWizardSlice = createSlice({
    name: 'mobileWizard',
    initialState,
    reducers: {
        clearWizards: (state) => {
            Object.keys(state).forEach((key) => {
                delete state[key];
            });
        },
        resetWizard: (state, action: PayloadAction<string>) => {
            delete state[action.payload];
        },
        initialiseWizard: (
            state,
            action: PayloadAction<{
                key: string;
                init: {
                    functionality: Functionality;
                    functionalitySettings: Setting[];
                    wizard: Wizard;
                    steps: Step[];
                    from?: string;
                    returnHistoryIndex?: number;
                    data?: MobileWizardStateData;
                };
            }>
        ) => {
            state[action.payload.key] = {
                ...action.payload.init,
                data: action.payload.init.data || {},
                history: [],
                currentScreen: action.payload.init.wizard.start_screen
            };
        },
        updateData: (
            state,
            action: PayloadAction<{
                key: string;
                data: MobileWizardStateData;
            }>
        ) => {
            if (state[action.payload.key]) {
                state[action.payload.key].data = {
                    ...state[action.payload.key].data,
                    ...action.payload.data
                };
            }
        },
        updateFunctionalityContext: (
            state,
            action: PayloadAction<{
                key: string;
                functionality: Functionality;
                functionalitySettings: Setting[];
            }>
        ) => {
            if (state[action.payload.key]) {
                state[action.payload.key].functionality = action.payload.functionality;
                state[action.payload.key].functionalitySettings = action.payload.functionalitySettings;
            }
        },
        updateWizardSteps: (
            state,
            action: PayloadAction<{
                key: string;
                steps: Step[];
            }>
        ) => {
            if (state[action.payload.key]) {
                state[action.payload.key].steps = action.payload.steps;
            }
        },
        clearData: (state, action: PayloadAction<string>) => {
            if (state[action.payload]) {
                state[action.payload].data = {};
            }
        },
        updateCurrentScreen: (state, action: PayloadAction<{ key: string; screen: ScreenType; preserveHistory?: boolean }>) => {
            if (state[action.payload.key]) {
                if (action.payload.preserveHistory) {
                    state[action.payload.key].currentScreen = action.payload.screen;
                    return;
                }

                if (!state[action.payload.key].history) state[action.payload.key].history = [];

                const historyIndex = state[action.payload.key].history!.lastIndexOf(action.payload.screen);
                if (historyIndex >= 0) {
                    state[action.payload.key].history = state[action.payload.key].history!.slice(0, historyIndex);
                } else if (state[action.payload.key].currentScreen && state[action.payload.key].currentScreen !== action.payload.screen)
                    state[action.payload.key].history!.push(state[action.payload.key].currentScreen);

                state[action.payload.key].currentScreen = action.payload.screen;
            }
        }
    }
});

export const mobileWizardStore = { wizards: mobileWizardSlice.reducer };
export const { initialiseWizard, updateData, updateFunctionalityContext, updateWizardSteps, clearData, resetWizard, clearWizards, updateCurrentScreen } =
    mobileWizardSlice.actions;
